import Vue, { CreateElement } from 'vue'
import { Component, Prop } from 'vue-property-decorator'

import { Caption, H5, Subtitle2 } from '@src/elements/typography/typography'

import { Modal } from '@src/elements/modal/modal'
import { Icon } from '@src/elements/icon/icon'
import { Divider } from '@src/elements/divider/divider'
import { ExerciseScore } from './exercise-score-chart/exercise-score'
import { ExerciseHistory } from '@src/routes/members/components/results-history/exercise-history-popup/exercise-history-chart/exercise-history'

import { ExerciseResponse } from '@src/data/exercise-response'
import { ExerciseResult, ExerciseResults, GraphAverageResults } from '@src/data/workout-results'

import { ExerciseService } from '@src/service/exercise'
import { exerciseName } from '@src/util/exercise-name'

import './exercise-info-history.sass'

@Component
export class ExerciseInfoHistory extends Vue {

  @Prop({ required: true })
  showModal: boolean

  @Prop({ required: true })
  exercise: ExerciseResponse

  @Prop({ required: true })
  result: ExerciseResult

  @Prop({ required: false })
  closeCallback: () => void

  windowWidth: number = window.innerWidth

  // tslint:disable-next-line:no-unused-variable
  private lastTenResultsForExercise: ExerciseResults

  private lastTenResultsGraphData: GraphAverageResults

  constructor () {
    super()
    this.lastTenResultsForExercise = new ExerciseResults()
    this.lastTenResultsGraphData = new GraphAverageResults()
  }

  created () {
    window.addEventListener('orientationchange', this.updateSize)
    window.addEventListener('resize', this.updateSize)
    this.windowWidth = window.innerWidth
  }

  destroyed () {
    window.removeEventListener('orientationchange', this.updateSize)
    window.removeEventListener('resize', this.updateSize)
  }

  updateSize () {
    this.windowWidth = window.innerWidth
  }

  async mounted () {
    await this.getGraphResults()
    return this.getExerciseResults()
  }

  getExerciseResults () {
    return new ExerciseService().getExerciseResults(this.$route.params.id, this.exercise.id.toString())
      .then(response => this.lastTenResultsForExercise = response)
  }

  getGraphResults () {
    return new ExerciseService().getExerciseGraphData(this.$route.params.id, this.exercise.id.toString())
      .then(response => {
        this.lastTenResultsGraphData = response
      })
  }

  getPositiveNegativeFeedback (h: CreateElement) {
    let positiveFeedback: any[] = []
    let negativeFeedback: any[] = []
    if (this.result && this.result.feedback) {
      if (this.result.feedback.positive) {
        positiveFeedback = this.result.feedback.positive.map(tag => {
          if (tag.key && tag.key !== 'INVAR_KEY_WRONG_MOVEMENT' && tag.key !== 'INVAR_KEY_TOO_FAST') {
            return <div><Caption>{this.$t(`${tag.key}.POSITIVE`)}</Caption></div>
          }
        })
      }
      if (this.result.feedback.negative) {
        negativeFeedback = this.result.feedback.negative.map(tag => {
          if (tag.key === 'INVAR_KEY_WRONG_MOVEMENT' || tag.key === 'INVAR_KEY_TOO_FAST') {
            return <div><Caption>{this.$t(`${tag.key}`)}</Caption></div>
          } else if (tag.key) {
            return <div><Caption>{this.$t(`${tag.key}.NEGATIVE`)}</Caption></div>
          }
        })
      }
    }
    return { positiveFeedback, negativeFeedback }
  }

  getScoresCurrentWorkout () {
    let precisionScores: any[] = []
    let tempoScores: any[] = []
    let rangeScores: any[] = []

    // create separate arrays out of the exercise results
    if (this.result && this.result.repetitions) {
      this.result.repetitions.forEach((result, index) => {
        if (result.precisionScore !== null) {
          precisionScores.push([index + 1, result.precisionScore])
        }
        if (result.tempoScore !== null) {
          tempoScores.push([index + 1, result.tempoScore])
        }
        if (result.rangeScore !== null) {
          rangeScores.push([index + 1, result.rangeScore])
        }
      })
    }
    return {
      precisionScoresCurrentWorkout: precisionScores,
      tempoScoresCurrentWorkout: tempoScores,
      rangeScoresCurrentWorkout: rangeScores
    }
  }

  getHistoryScoresCurrentExercise () {
    let precisionScores: any[] = []
    let tempoScores: any[] = []
    let rangeScores: any[] = []
    let pixScores: any[] = []
    let startDate = ''
    let endDate = ''
    let scoresLength = 0

    // create separate arrays out of the exercise results
    if (this.lastTenResultsGraphData && this.lastTenResultsGraphData.averageStats) {
      this.lastTenResultsGraphData.averageStats.forEach((result) => {
        if (result.startedAt) {
          if (result.precisionScoreAvg !== null) {
            precisionScores.push([new Date(result.startedAt).getTime(), result.precisionScoreAvg])
          }
          if (result.tempoScoreAvg !== null) {
            tempoScores.push([new Date(result.startedAt).getTime(), result.tempoScoreAvg])
          }
          if (result.rangeScoreAvg !== null) {
            rangeScores.push([new Date(result.startedAt).getTime(), result.rangeScoreAvg])
          }
          if (result.pixformanceScoreAvg !== null) {
            pixScores.push([new Date(result.startedAt).getTime(), result.pixformanceScoreAvg])
          }
        }
      })

      // all lists should have the same length (if score is null => exercise untracked => all scores null)
      scoresLength = precisionScores.length
      startDate = precisionScores.length > 0 ? precisionScores[0][0] : ''
      endDate = precisionScores.length > 0 ? precisionScores[precisionScores.length - 1][0] : ''
    }
    return {
      precisionScores: precisionScores,
      tempoScores: tempoScores,
      rangeScores: rangeScores,
      pixScores: pixScores,
      totalDays: scoresLength,
      startDate: startDate,
      endDate: endDate
    }
  }

  render (h: CreateElement) {
    const videoURLs = this.exercise.getVideoURLs()
    let { positiveFeedback, negativeFeedback } = this.getPositiveNegativeFeedback(h)
    let { precisionScoresCurrentWorkout, tempoScoresCurrentWorkout, rangeScoresCurrentWorkout } = this.getScoresCurrentWorkout()
    let scoresHistory = this.getHistoryScoresCurrentExercise()
    let repetitonGoal = this.result.repetitionGoal
    let repetitionCount = this.result.repetitionCount
    let isMoreThanHalf = false
    let isZeroReps = repetitionCount === 0

    if (repetitonGoal && repetitionCount) {
      isMoreThanHalf = repetitionCount > (repetitonGoal / 2)
    }
    return <Modal title={exerciseName(this, this.exercise)} show={this.showModal} closeCallback={this.closeCallback} cssClass='exercise-info-history'>
      <div class='exercise-history-portrait-modal-wrapper'>
        <div class='row'>
          <div class='info-img'>
            <video autoplay='autoplay' loop='loop' muted>
              <source src={videoURLs.webm} type='video/webm' />
              <source src={videoURLs.mp4} type='video/mp4' />
              Your browser does not support the video tag.
            </video>
          </div>
          <div className='info-container portrait-half-width'>
            <div>
              {isZeroReps ?
                <H5 class='info-title-red'>{this.$t('PAGE.MEMBERS.TRAINING.HISTORY.TRY_AGAIN')}</H5> :
                !isMoreThanHalf ?
                  <H5 class='info-title-red'>{this.$t('PAGE.MEMBERS.TRAINING.HISTORY.TRY_AGAIN')}</H5> :
                  <H5 class='info-title'>{this.$t('PAGE.MEMBERS.TRAINING.HISTORY.WELL_DONE')}</H5>
              }
            </div>
            {repetitonGoal && repetitonGoal > 0 && !isZeroReps && (positiveFeedback.length || negativeFeedback.length) ?
              <div>
                <div className='feedback'>
                  <div className='positive'>
                    <div className='list'>
                      {positiveFeedback}
                    </div>
                  </div>
                  <div className='negative'>
                    <div className='list'>
                      {negativeFeedback}
                    </div>
                  </div>
                </div>
              </div>
              : ''}
          </div>
        </div>
        <div class='row'>
          {(this.windowWidth >= 600 && this.windowWidth < 1025) && (<div class='info-container visible-on-tablet'>
            <Divider/>
            <div class='scores'>
              <div class='score'>
                <Icon name={'brand/accuracy'}/>
                <Subtitle2><b>{`${this.$t('PAGE.MEMBERS.TRAINING.HISTORY.ACCURACY')}`}</b></Subtitle2>
                <Caption>{this.$t('PAGE.MEMBERS.TRAINING.HISTORY.REP_IN_POINTS')}</Caption>
              </div>
              <div class='score'>
                <Icon name={'brand/tempo'}/>
                <Subtitle2><b>{`${this.$t('TEMPO.TITLE')}`}</b></Subtitle2>
                <Caption>{this.$t('PAGE.MEMBERS.TRAINING.HISTORY.REP_IN_POINTS')}</Caption>
              </div>
              <div class='score'>
                <Icon name={'brand/range-of-motion'}/>
                <Subtitle2><b>{`${this.$t('PAGE.MEMBERS.TRAINING.HISTORY.RANGE_OF_MOTION')}`}</b></Subtitle2>
                <Caption>{this.$t('PAGE.MEMBERS.TRAINING.HISTORY.REP_IN_POINTS')}</Caption>
              </div>
            </div>
            <div class='scores'>
              <div class='score'>
                {precisionScoresCurrentWorkout ?
                  <ExerciseScore emptyMessage={this.$t('PAGE.MEMBERS.TRAINING.HISTORY.EMPTY_GRAPH_PRECISION_SCORE')}
                                 scores={precisionScoresCurrentWorkout}/> : ''}
              </div>
              <div class='score'>
                {tempoScoresCurrentWorkout ?
                  <ExerciseScore emptyMessage={this.$t('PAGE.MEMBERS.TRAINING.HISTORY.EMPTY_GRAPH_TEMPO_SCORE')}
                                 scores={tempoScoresCurrentWorkout}/> : ''}
              </div>
              <div class='score'>
                {rangeScoresCurrentWorkout ?
                  <ExerciseScore emptyMessage={this.$t('PAGE.MEMBERS.TRAINING.HISTORY.EMPTY_GRAPH_RANGE_SCORE')}
                                 scores={rangeScoresCurrentWorkout}/> : ''}
              </div>
            </div>
            <Divider/>
            {(scoresHistory) && <ExerciseHistory scores={scoresHistory}/>}
          </div>)}
          {this.windowWidth < 600 && (<div class='info-container visible-on-mobile'>
            <Divider/>
            <div class='scores column-scores'>
              <div class='column-score'>
                <div class='score'>
                  <Icon name={'brand/accuracy'}/>
                  <Subtitle2><b>{`${this.$t('PAGE.MEMBERS.TRAINING.HISTORY.ACCURACY')}`}</b></Subtitle2>
                  <Caption>{this.$t('PAGE.MEMBERS.TRAINING.HISTORY.REP_IN_POINTS')}</Caption>
                </div>
                <div class='score'>
                  {precisionScoresCurrentWorkout ?
                    <ExerciseScore emptyMessage={this.$t('PAGE.MEMBERS.TRAINING.HISTORY.EMPTY_GRAPH_PRECISION_SCORE')}
                                   scores={precisionScoresCurrentWorkout}/> : ''}
                </div>
              </div>
              <div class='column-score'>
                <div class='score'>
                  <Icon name={'brand/tempo'}/>
                  <Subtitle2><b>{`${this.$t('TEMPO.TITLE')}`}</b></Subtitle2>
                  <Caption>{this.$t('PAGE.MEMBERS.TRAINING.HISTORY.REP_IN_POINTS')}</Caption>
                </div>
                <div class='score'>
                  {tempoScoresCurrentWorkout ?
                    <ExerciseScore emptyMessage={this.$t('PAGE.MEMBERS.TRAINING.HISTORY.EMPTY_GRAPH_TEMPO_SCORE')}
                                   scores={tempoScoresCurrentWorkout}/> : ''}
                </div>
              </div>
              <div class='column-score'>
                <div class='score'>
                  <Icon name={'brand/range-of-motion'}/>
                  <Subtitle2><b>{`${this.$t('PAGE.MEMBERS.TRAINING.HISTORY.RANGE_OF_MOTION')}`}</b></Subtitle2>
                  <Caption>{this.$t('PAGE.MEMBERS.TRAINING.HISTORY.REP_IN_POINTS')}</Caption>
                </div>
                <div class='score'>
                  {rangeScoresCurrentWorkout ?
                    <ExerciseScore emptyMessage={this.$t('PAGE.MEMBERS.TRAINING.HISTORY.EMPTY_GRAPH_RANGE_SCORE')}
                                   scores={rangeScoresCurrentWorkout}/> : ''}
                </div>
              </div>
            </div>
            <Divider/>
            {(scoresHistory) && <ExerciseHistory scores={scoresHistory}/>}
          </div>)}
        </div>
      </div>
      <div class='info-img hidden-on-devices'>
        <video autoplay='autoplay' loop='loop' muted>
          <source src={videoURLs.webm} type='video/webm' />
          <source src={videoURLs.mp4} type='video/mp4' />
          Your browser does not support the video tag.
        </video>
      </div>
      {this.windowWidth > 1024 && (<div class='info-container hidden-on-devices'>
        <div>
          { isZeroReps ?
            <H5 class='info-title-red'>{this.$t('PAGE.MEMBERS.TRAINING.HISTORY.TRY_AGAIN')}</H5> :
            !isMoreThanHalf ?
              <H5 class='info-title-red'>{this.$t('PAGE.MEMBERS.TRAINING.HISTORY.TRY_AGAIN')}</H5> :
              <H5 class='info-title'>{this.$t('PAGE.MEMBERS.TRAINING.HISTORY.WELL_DONE')}</H5>
          }
        </div>
        { repetitonGoal && repetitonGoal > 0 && !isZeroReps && (positiveFeedback.length || negativeFeedback.length) ?
          <div>
            <div class='feedback'>
              <div class='positive'>
                <div class='list'>
                  {positiveFeedback}
                </div>
              </div>
              <div class='negative'>
                <div class='list'>
                  {negativeFeedback}
                </div>
              </div>
            </div>
          </div>
          : '' }
      </div>)}
      {this.windowWidth > 1024 && (<div class='info-container info-scores hidden-on-devices'>
        <Divider/>
        <div class='scores'>
          <div class='score'>
            <Icon name={'brand/accuracy'}/>
            <Subtitle2><b>{`${this.$t('PAGE.MEMBERS.TRAINING.HISTORY.ACCURACY')}`}</b></Subtitle2>
            <Caption>{this.$t('PAGE.MEMBERS.TRAINING.HISTORY.REP_IN_POINTS')}</Caption>
          </div>
          <div class='score'>
            <Icon name={'brand/tempo'}/>
            <Subtitle2><b>{`${this.$t('TEMPO.TITLE')}`}</b></Subtitle2>
            <Caption>{this.$t('PAGE.MEMBERS.TRAINING.HISTORY.REP_IN_POINTS')}</Caption>
          </div>
          <div class='score'>
            <Icon name={'brand/range-of-motion'}/>
            <Subtitle2><b>{`${this.$t('PAGE.MEMBERS.TRAINING.HISTORY.RANGE_OF_MOTION')}`}</b></Subtitle2>
            <Caption>{this.$t('PAGE.MEMBERS.TRAINING.HISTORY.REP_IN_POINTS')}</Caption>
          </div>
        </div>
        <div class='scores'>
          <div class='score'>
            {precisionScoresCurrentWorkout ?
              <ExerciseScore emptyMessage={this.$t('PAGE.MEMBERS.TRAINING.HISTORY.EMPTY_GRAPH_PRECISION_SCORE')}
                             scores={precisionScoresCurrentWorkout}/> : ''}
          </div>
          <div class='score'>
            {tempoScoresCurrentWorkout ?
              <ExerciseScore emptyMessage={this.$t('PAGE.MEMBERS.TRAINING.HISTORY.EMPTY_GRAPH_TEMPO_SCORE')}
                             scores={tempoScoresCurrentWorkout}/> : ''}
          </div>
          <div class='score'>
            {rangeScoresCurrentWorkout ?
              <ExerciseScore emptyMessage={this.$t('PAGE.MEMBERS.TRAINING.HISTORY.EMPTY_GRAPH_RANGE_SCORE')}
                             scores={rangeScoresCurrentWorkout}/> : ''}
          </div>
        </div>
        <Divider/>
        {(scoresHistory) && <ExerciseHistory scores={scoresHistory}/>}
      </div>)}
    </Modal>
  }
}
